import { useEffect, useRef, useState } from 'react';
import { serverNow } from '../utils/serverClock';
import { formatCountdown } from '../utils/countdownFormat';

/** Standard tick cadence. */
const TICK_MS = 1000;
/** Tighter cadence once under a minute remains, so the final seconds feel live. */
const FAST_TICK_MS = 200;
const FAST_TICK_THRESHOLD_MS = 60_000;

export interface UseServerCountdownOptions {
  onExpire?: () => void;
}

export interface UseServerCountdownResult {
  /**
   * Milliseconds remaining until the target, or null when there is no
   * target. Derived fresh from the absolute deadline on every tick.
   */
  remainingMs: number | null;
  expired: boolean;
  label: string;
}

/**
 * Counts down to an absolute server-clock deadline.
 *
 * The remaining time is recomputed from `target - serverNow()` on every
 * tick rather than decremented from a local counter — a throttled or
 * backgrounded tab therefore shows the correct value the instant it wakes,
 * instead of a value that silently drifted while asleep.
 */
export function useServerCountdown(
  targetIso: string | null,
  opts?: UseServerCountdownOptions
): UseServerCountdownResult {
  const targetMs = targetIso === null ? NaN : Date.parse(targetIso);
  const hasTarget = !Number.isNaN(targetMs);

  const [remainingMs, setRemainingMs] = useState<number | null>(() =>
    hasTarget ? Math.max(0, targetMs - serverNow()) : null
  );

  // Kept in a ref, not a dependency, so a changing inline lambda can't cause
  // onExpire to fire more than once for the same target.
  const onExpireRef = useRef(opts?.onExpire);
  onExpireRef.current = opts?.onExpire;

  useEffect(() => {
    if (!hasTarget) {
      setRemainingMs(null);
      return undefined;
    }

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let currentTickMs: number | null = null;
    let firedExpire = false;

    const scheduleTick = (tickMs: number) => {
      if (currentTickMs === tickMs) {
        return;
      }
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      currentTickMs = tickMs;
      intervalId = setInterval(tick, tickMs);
    };

    function tick() {
      const remaining = Math.max(0, targetMs - serverNow());
      setRemainingMs(remaining);

      if (remaining <= 0) {
        if (intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
        if (!firedExpire) {
          firedExpire = true;
          onExpireRef.current?.();
        }
        return;
      }

      scheduleTick(remaining <= FAST_TICK_THRESHOLD_MS ? FAST_TICK_MS : TICK_MS);
    }

    tick();

    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [targetMs, hasTarget]);

  return {
    remainingMs,
    expired: remainingMs !== null && remainingMs <= 0,
    label: remainingMs === null ? '' : formatCountdown(remainingMs)
  };
}
